import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState
} from 'react'
import { UserModel, get_user_profile_api } from '../service/user'
import { settings_api } from '../service/settings'
import { refresh_token_api } from '../service/auth'
import { setupAxios } from '../service/api-interseptor/axiosInstance'
import axios from 'axios'

type AuthContextProps = {
  isLoggedIn: boolean
  token: string | null
  currentUser: UserModel
  login: (token: string, refresh_token: string) => void
  logout: () => void
  refresh: () => void
}

const initialState = {
  isLoggedIn: !!localStorage.getItem('token'),
  token: localStorage.getItem('token'),
  currentUser:
    localStorage.getItem('user') ?
      JSON.parse(localStorage.getItem('user')!)
    : {},
  login: (token: string, refresh_token: string) => {},
  logout: () => {},
  refresh: () => {}
}

const AuthContext = createContext<AuthContextProps>(initialState)

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState(initialState)
  const login = async (token: string, refresh_token: string) => {
    localStorage.setItem('token', token)
    const user = await requestUser()
    setState({ ...state, token, isLoggedIn: true, currentUser: user })
    localStorage.setItem('refresh_token', refresh_token)
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    } else {
      localStorage.removeItem('user')
    }
  }
  const logout = useCallback(() => {
    setState({ ...initialState, token: null, isLoggedIn: false })
    localStorage.clear()
  }, [])
  const refresh = useCallback(async () => {
    const refresh_token = localStorage.getItem('refresh_token')
    if (refresh_token) {
      try {
        const auth = await refresh_token_api(refresh_token)
        if (auth) login(auth.token, auth.refresh_token)
        else logout()
      } catch (error) {
        console.error('Error refreshing token', error)
        logout()
      }
    }
    // eslint-disable-next-line
  }, [])
  const requestUser = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return logout()
      const currentUser = await get_user_profile_api()
      if (currentUser) {
        localStorage.setItem('user', JSON.stringify(currentUser))
        setState((prev) => ({ ...prev, currentUser }))
      }
      const settings = await settings_api({ queryKey: [] })
      if (settings) localStorage.setItem('settings', JSON.stringify(settings))
      return currentUser
    } catch (error) {
      logout()
    }
  }
  useLayoutEffect(() => {
    setupAxios(axios, logout, refresh)
    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    if (state.isLoggedIn && state.token) requestUser()
    // eslint-disable-next-line
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  return useContext(AuthContext)
}

export { AuthProvider, useAuth }
